import { Auth } from 'src/services/authAccount/auth.service';
import { accessInfo, AccessTypes } from 'src/model/access';
import { hasAccess } from 'src/model/hasAccess';
import accessObjectHelper from 'layouts/helpres/accessObjectHelper';
import {
    ADMIN_PREFIX,
    ADMIN_PREFIX_SLASH,
    EXTERNAL_PREFIX,
    ADMIN_LOGGED_KEY,
    AUTH_PATHS,
} from './constants';
import { lsGet, lsRemove } from './helpers';

/**
 * Id оператора для запроса доступов. Источник истины — сессия: его кладёт
 * авторизация (operatorKey → localStorage / campusOperatorId), и к этому
 * моменту он уже проставлен. Стор — фолбэк для повторных переходов.
 *
 * @param {any} store
 * @param {boolean} [isExternal=false]
 * @returns {String|Number|null}
 */
function resolveOperatorId (store, isExternal = false) {
    return (isExternal ? sessionStorage.getItem('campusOperatorId') : null)
        || localStorage.getItem('operatorId')
        || store.getters['operatorPermissions/getId'];
}

function mergeRouteParams (from, to) {
    const query = { ...from.query, ...to.query };
    const params = { ...from.params, ...to.params };

    return { query, params };
}

async function authorize ({ store, to }) {
    const auth = new Auth({ store, query: to.query });
    await auth.authorization();
}

function notifyExternalRouteChanged ({ from, to }) {
    setTimeout(() => {
        try {
            const msg = {
                type: 'route-changed',
                from,
                to,
            };
            window.top.postMessage(JSON.stringify(msg), '*');
        } catch (e) {
            console.warn(e);
        }
    }, 500);
}

/**
 *
 * @param from
 * @param to
 * @returns {boolean}
 */
function isExternalRoute (from, to) {
    if (from.path.startsWith(EXTERNAL_PREFIX) && to.path.startsWith(ADMIN_PREFIX_SLASH)) {
    // Значит переходим к другой странице в рамках админки ЛМС
        to.path = to.path.replace(ADMIN_PREFIX_SLASH, EXTERNAL_PREFIX);

        notifyExternalRouteChanged({ from, to });

        return true;
    }

    return false;
}

/**
 * Admin area rule.
 *
 * - Applies only for routes under ADMIN_PREFIX.
 * - Performs external route mapping for embedded LMS admin.
 * - Enforces admin login state, and runs Auth.authorization().
 *
 * @param {object} ctx
 * @param {import('vue-router').RouteLocationNormalized} ctx.to
 * @param {import('vue-router').RouteLocationNormalized} ctx.from
 * @param {any} ctx.store
 * @returns {Promise<false|true|object>} false to continue; true to allow; route object to redirect.
 */
export async function ruleAdmin ({ to, from, store }) {
    // console.log('🔍 админские роуты');

    if (!to.path.startsWith(ADMIN_PREFIX)) {
        return false;
    }

    if (isExternalRoute(from, to)) {
        const { query, params } = mergeRouteParams(from, to);

        return { path: to.path, query, params };
    }

    const operatorKey = to?.query?.k;

    if (operatorKey) {
        lsRemove(ADMIN_LOGGED_KEY);
    }

    let isAdminLogged = lsGet(ADMIN_LOGGED_KEY);

    if (to.path.startsWith(`${ADMIN_PREFIX}/search`)) {
        isAdminLogged = true;
    }

    if (!isAdminLogged) {
        return { path: '/admin-login', query: to.query, params: to.params };
    }

    if (!to.path.startsWith(`${ADMIN_PREFIX}/messages`) && isAdminLogged === '2') {
        return { path: `${ADMIN_PREFIX}/messages` };
    }

    if (to.path.startsWith(`${ADMIN_PREFIX}/mockup`) && !store.state.platform.settings.usePortfolio) {
        return { path: `${ADMIN_PREFIX}/users` };
    }

    await authorize({ store, to });

    // isExternal — права оператора Кампуса, загруженные в этой вкладке страницами Оператора (OperatorAdminHost):
    // их перегружает load(), обновления стора мало — isSuperadmin остался бы false
    if (accessInfo.loaded && !accessInfo.isExternal) {
        // сессия уже поднята — обновляем доступы, они могли измениться
        await store.dispatch('operatorPermissions/fetch', resolveOperatorId(store));
    } else {
        // первый вход: load() сам наполняет стор, второй запрос не нужен
        await accessInfo.load();
    }

    // Сессия не поднялась (оператор не найден) — load() уже инициировал разлогин,
    // доступы проверять незачем.
    if (!accessInfo.loaded) {
        return true;
    }

    const operatorPermissions = store.getters['operatorPermissions/getData'];
    const isExternal = to.path.includes('/external/');
    const data = accessObjectHelper(to.path, to.query, to.params, to.name, { pageTitle: '' }) || {};

    if (Object.keys(data).length && !hasAccess({
        isExternal,
        operatorPermissions,
    }, data.type, data.id, AccessTypes.Read)) {
        return { path: `${ADMIN_PREFIX}` };
    }

    return true;
}

/**
 * Ensures user is authorized for non-auth routes.
 *
 * @param {object} ctx
 * @param {import('vue-router').RouteLocationNormalized} ctx.to
 * @param {any} ctx.store
 * @returns {Promise<false|true>} true for auth pages; otherwise performs authorization and returns false.
 */
export async function ruleEnsureAuth ({ to, store }) {
    // console.log('🔍 проверка авторизации');
    const path = to.path;

    if (AUTH_PATHS.has(path)) {
        return true;
    }

    await authorize({ store, to });

    return false;
}

export async function ruleExternal ({ to, store }) {
    if (!to.path.startsWith(EXTERNAL_PREFIX)) {
        return false;
    }

    if (accessInfo.loaded) {
        await store.dispatch('operatorPermissions/fetch', resolveOperatorId(store, true));
    } else {
        await accessInfo.load(to.query.o, true);
    }

    return true;
}
